import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  increment, 
  serverTimestamp,
  collection,
  getDocs
} from 'firebase/firestore';
import errorNotificationService from '../services/ErrorNotificationService';

// Import original Firebase functions for internal usage (to avoid recursion)
import { 
  doc as originalDoc,
  setDoc as originalSetDoc,
  getDoc as originalGetDoc,
  updateDoc as originalUpdateDoc
} from 'firebase/firestore';

// Plan limits configuration - Standardized with backoffice
export const PLAN_LIMITS = {
  starter: {
    operations: 3000,
    storage: 100, // MB
    price: 29,
    name: 'Starter'
  },
  basic: {
    operations: 10000,
    storage: 300, // MB
    price: 49,
    name: 'Basic'
  },
  pro: {
    operations: 50000,
    storage: 1024, // 1 GB in MB
    price: 199,
    name: 'Pro'
  },
  enterprise: {
    operations: 200000,
    storage: 5120, // 5 GB in MB
    price: 399,
    name: 'Enterprise'
  },
  // Legacy plan support for backward compatibility
  max: {
    operations: 200000,
    storage: 5120, // 5 GB in MB
    price: 399,
    name: 'Enterprise' // Map max to enterprise
  }
};

// Operation buffer to reduce Firebase writes
let operationBuffer = {};
let storageBuffer = {};
// Flush timers per company (so low volume usage still gets persisted)
let operationFlushTimers = {};

function normalizeOperationType(operationType) {
  const t = String(operationType || '').toLowerCase();
  if (t === 'read' || t === 'reads') return 'reads';
  if (t === 'write' || t === 'writes') return 'writes';
  if (t === 'delete' || t === 'deletes') return 'deletes';
  return 'reads';
}

// Get current month key (YYYY-MM)
export const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Get current date key (YYYY-MM-DD)
const getCurrentDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Get company's current plan
 */
export const getCompanyPlan = async (db, companyId) => {
  try {
    const subDoc = await originalGetDoc(originalDoc(db, `companies/${companyId}/subscription_details/subscription`));
    if (subDoc.exists()) {
      const data = subDoc.data();
      // Support both field names for compatibility
      return data?.planId || data?.plan || 'starter';
    }
    return 'starter'; // Default plan
  } catch (error) {
    console.error('Error fetching company plan:', error);
    return 'starter';
  }
};

/** Sum reads + writes + deletes from a usage month document. */
export function getUsageBreakdownTotal(data) {
  if (!data?.breakdown) return 0;
  const b = data.breakdown;
  return (b.reads || 0) + (b.writes || 0) + (b.deletes || 0);
}

/** Sum operations from dailyUsage map. */
export function getUsageDailyTotal(data) {
  if (!data?.dailyUsage || typeof data.dailyUsage !== 'object') return 0;
  let dailySum = 0;
  for (const day of Object.values(data.dailyUsage)) {
    if (day && typeof day === 'object') {
      dailySum += day.operations || 0;
    }
  }
  return dailySum;
}

/**
 * Canonical billable operation count for subscription/statistics.
 * Prefer breakdown (R+W+D) — single source of truth when typed counters exist.
 */
export function resolveCanonicalOperations(data) {
  if (!data) return 0;
  const breakdownSum = getUsageBreakdownTotal(data);
  if (breakdownSum > 0) return breakdownSum;
  const ops = typeof data.operations === 'number' ? data.operations : 0;
  if (ops > 0) return ops;
  return getUsageDailyTotal(data);
}

/**
 * @deprecated Use resolveCanonicalOperations — kept for backward compatibility.
 */
export function estimateOperationsFromUsageMonthDoc(data) {
  return resolveCanonicalOperations(data);
}

/** Daily rows sorted oldest → newest for charts/tables. */
export function getDailyUsageRows(dailyUsage = {}) {
  if (!dailyUsage || typeof dailyUsage !== 'object') return [];
  return Object.entries(dailyUsage)
    .map(([date, day]) => ({
      date,
      operations: day?.operations || 0,
      storageMb: typeof day?.storage === 'number' ? day.storage : 0
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Monday-start week key (YYYY-MM-DD of that Monday). */
export function getWeekStartKey(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setDate(diff);
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, '0');
  const dd = String(monday.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function formatWeekLabel(weekStartKey) {
  const d = new Date(`${weekStartKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return weekStartKey;
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  const opts = { day: 'numeric', month: 'short' };
  return `${d.toLocaleDateString('en-IN', opts)} – ${end.toLocaleDateString('en-IN', { ...opts, year: 'numeric' })}`;
}

/** Aggregate daily rows into weekly buckets (Monday–Sunday). */
export function aggregateDailyToWeekly(dailyRows = []) {
  const byWeek = {};
  for (const d of dailyRows) {
    const weekStart = getWeekStartKey(d.date);
    if (!byWeek[weekStart]) {
      byWeek[weekStart] = {
        weekStart,
        weekLabel: formatWeekLabel(weekStart),
        operations: 0,
        storageMb: 0,
        startDate: d.date,
        endDate: d.date,
        dayCount: 0
      };
    }
    const w = byWeek[weekStart];
    w.operations += d.operations;
    w.storageMb += d.storageMb;
    w.dayCount += 1;
    if (d.date < w.startDate) w.startDate = d.date;
    if (d.date > w.endDate) w.endDate = d.date;
  }
  return Object.values(byWeek).sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

/** Firestore-friendly weekly map from dailyUsage. */
export function buildWeeklyUsageMap(dailyUsage = {}) {
  const weekly = {};
  for (const w of aggregateDailyToWeekly(getDailyUsageRows(dailyUsage))) {
    weekly[w.weekStart] = {
      operations: w.operations,
      storage: Math.round(w.storageMb * 100) / 100,
      startDate: w.startDate,
      endDate: w.endDate
    };
  }
  return weekly;
}

export function filterDailyRowsByRange(dailyRows, fromDate, toDate) {
  return dailyRows.filter((r) => {
    if (fromDate && r.date < fromDate) return false;
    if (toDate && r.date > toDate) return false;
    return true;
  });
}

function scaleBreakdownToOperations(oldBreakdown, newOperations) {
  const oldBreakdownSafe = oldBreakdown || {};
  const oldSum =
    (oldBreakdownSafe.reads || 0) +
    (oldBreakdownSafe.writes || 0) +
    (oldBreakdownSafe.deletes || 0);

  if (newOperations <= 0) {
    return { reads: 0, writes: 0, deletes: 0, uploads: oldBreakdownSafe.uploads || 0 };
  }
  if (oldSum <= 0) {
    return { reads: 0, writes: newOperations, deletes: 0, uploads: oldBreakdownSafe.uploads || 0 };
  }

  const factor = newOperations / oldSum;
  const newBreakdown = {
    reads: Math.round((oldBreakdownSafe.reads || 0) * factor),
    writes: Math.round((oldBreakdownSafe.writes || 0) * factor),
    deletes: Math.round((oldBreakdownSafe.deletes || 0) * factor),
    uploads: oldBreakdownSafe.uploads || 0
  };
  const breakdownTotal = newBreakdown.reads + newBreakdown.writes + newBreakdown.deletes;
  const diff = newOperations - breakdownTotal;
  if (diff !== 0) {
    newBreakdown.writes = Math.max(0, newBreakdown.writes + diff);
  }
  return newBreakdown;
}

/**
 * Re-sync month totals from the daily activity log (admin / dev repair).
 */
export async function recalculateUsageMonthFromDaily(db, companyId, monthId, options = {}) {
  const { sinceDate = null, clearBeforeDate = false } = options;
  const usageRef = originalDoc(db, `companies/${companyId}/usage/${monthId}`);
  const snap = await originalGetDoc(usageRef);
  if (!snap.exists()) {
    return { success: false, error: 'Usage record not found for this month.' };
  }

  const data = snap.data();
  let dailyUsage = { ...(data.dailyUsage || {}) };

  if (sinceDate && clearBeforeDate) {
    for (const dateKey of Object.keys(dailyUsage)) {
      if (dateKey < sinceDate) {
        delete dailyUsage[dateKey];
      }
    }
  }

  const allDaily = getDailyUsageRows(dailyUsage);
  const includedDaily =
    sinceDate && !clearBeforeDate
      ? allDaily.filter((r) => r.date >= sinceDate)
      : allDaily;

  const newOperations = includedDaily.reduce((s, r) => s + r.operations, 0);
  const newBreakdown = scaleBreakdownToOperations(data.breakdown, newOperations);
  const weeklyUsage = buildWeeklyUsageMap(dailyUsage);

  await originalSetDoc(
    usageRef,
    {
      operations: newOperations,
      breakdown: newBreakdown,
      dailyUsage,
      weeklyUsage,
      lastRecalculatedFromDaily: serverTimestamp(),
      recalculatedSinceDate: sinceDate || null,
      clearedDailyBefore: clearBeforeDate && sinceDate ? sinceDate : null
    },
    { merge: true }
  );

  return {
    success: true,
    monthId,
    operations: newOperations,
    breakdown: newBreakdown,
    daysIncluded: includedDaily.length,
    daysRemoved: clearBeforeDate && sinceDate ? allDaily.length - includedDaily.length : 0,
    sinceDate,
    clearBeforeDate
  };
}

/** Remove inflated daily usage before a cutoff and rebuild month totals (admin repair). */
export async function clearUsageBeforeDate(db, companyId, monthId, cutoffDate) {
  if (!cutoffDate) {
    return { success: false, error: 'Cutoff date is required (YYYY-MM-DD).' };
  }
  return recalculateUsageMonthFromDaily(db, companyId, monthId, {
    sinceDate: cutoffDate,
    clearBeforeDate: true
  });
}

/** In-memory tracking buffer snapshot (dev / analysis). */
export const getTrackingBufferState = () => ({
  buffers: JSON.parse(JSON.stringify(operationBuffer)),
  pendingFlushCompanies: Object.keys(operationFlushTimers)
});

/**
 * Sum tracked operations across all `companies/{companyId}/usage/*` month documents (all-time in-app counter).
 */
export async function getLifetimeTrackedOperations(db, companyId) {
  const usageCol = collection(db, 'companies', companyId, 'usage');
  const snap = await getDocs(usageCol);
  const byMonth = {};
  let total = 0;
  snap.forEach((docSnap) => {
    const monthId = docSnap.id;
    const n = estimateOperationsFromUsageMonthDoc(docSnap.data());
    byMonth[monthId] = n;
    total += n;
  });
  return {
    total,
    byMonth,
    monthCount: snap.size
  };
}

/** Human-readable label for a usage doc id like `2026-04`. */
export function formatMonthIdToLabel(monthId) {
  if (!monthId || typeof monthId !== 'string') return monthId || '';
  const [y, m] = monthId.split('-');
  if (!y || !m) return monthId;
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  if (Number.isNaN(d.getTime())) return monthId;
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

/**
 * All monthly usage rows for charts/tables (sorted oldest → newest).
 */
export async function getCompanyUsageHistoryDetailed(db, companyId) {
  const usageCol = collection(db, 'companies', companyId, 'usage');
  const snap = await getDocs(usageCol);
  const rows = [];
  snap.forEach((docSnap) => {
    const monthId = docSnap.id;
    const data = docSnap.data();
    const b = data.breakdown || {};
    const dailyRows = getDailyUsageRows(data.dailyUsage);
    const weeklyRows =
      data.weeklyUsage && typeof data.weeklyUsage === 'object'
        ? aggregateDailyToWeekly(dailyRows).map((w) => {
            const stored = data.weeklyUsage[w.weekStart];
            if (stored && typeof stored.operations === 'number') {
              return { ...w, operations: stored.operations, storageMb: stored.storage || w.storageMb };
            }
            return w;
          })
        : aggregateDailyToWeekly(dailyRows);

    rows.push({
      monthId,
      monthLabel: formatMonthIdToLabel(monthId),
      operations: resolveCanonicalOperations(data),
      storedOperations: typeof data.operations === 'number' ? data.operations : 0,
      reads: b.reads || 0,
      writes: b.writes || 0,
      deletes: b.deletes || 0,
      storageMb: typeof data.storage === 'number' ? data.storage : 0,
      dailyUsage: data.dailyUsage || {},
      dailyRows,
      weeklyRows,
      lastUpdated: data.lastUpdated || null,
      audit: {
        lastRecalculatedFromDaily: data.lastRecalculatedFromDaily || null,
        recalculatedSinceDate: data.recalculatedSinceDate || null,
        clearedDailyBefore: data.clearedDailyBefore || null
      }
    });
  });
  rows.sort((a, b) => a.monthId.localeCompare(b.monthId));
  return rows;
}

/**
 * Roll up monthly rows by calendar year.
 */
export function aggregateUsageByYear(monthlyRows) {
  const byYear = {};
  for (const row of monthlyRows) {
    const year = row.monthId.slice(0, 4);
    if (!byYear[year]) {
      byYear[year] = {
        year,
        operations: 0,
        reads: 0,
        writes: 0,
        deletes: 0,
        monthsCount: 0,
        peakMonth: { monthId: null, monthLabel: null, operations: 0 }
      };
    }
    byYear[year].operations += row.operations;
    byYear[year].reads += row.reads;
    byYear[year].writes += row.writes;
    byYear[year].deletes += row.deletes;
    byYear[year].monthsCount += 1;
    if (row.operations > byYear[year].peakMonth.operations) {
      byYear[year].peakMonth = {
        monthId: row.monthId,
        monthLabel: row.monthLabel,
        operations: row.operations
      };
    }
  }
  return Object.values(byYear).sort((a, b) => a.year.localeCompare(b.year));
}

export const getCompanyUsage = async (db, companyId) => {
  try {
    const month = getCurrentMonth();
    const usageDoc = await originalGetDoc(originalDoc(db, `companies/${companyId}/usage/${month}`));
    
    if (usageDoc.exists()) {
      const data = usageDoc.data();
      return data;
    }
    
    return {
      month,
      operations: 0,
      storage: 0,
      breakdown: {
        reads: 0,
        writes: 0,
        deletes: 0,
        uploads: 0
      },
      dailyUsage: {},
      lastUpdated: null,
      limitExceeded: false
    };
  } catch (error) {
    console.error('Error fetching company usage:', error);
    return null;
  }
};

/**
 * Get total available quota (base + topups) from subscription
 */
export const getTotalAvailableQuota = async (db, companyId) => {
  try {
    const subscriptionRef = originalDoc(db, `companies/${companyId}/subscription_details/subscription`);
    const subscriptionSnap = await originalGetDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      return null;
    }
    
    const subscription = subscriptionSnap.data();
    
    // Check if new structure with totalEntitlements
    if (subscription.totalEntitlements) {
      return {
        firebase_operations: subscription.totalEntitlements.firebase_operations || 0,
        storage_mb: subscription.totalEntitlements.storage_mb || 0,
        sales_documents: subscription.totalEntitlements.sales_documents || 0,
        parties: subscription.totalEntitlements.parties || 0,
        hasTopups: (subscription.topups && subscription.topups.length > 0)
      };
    }
    
    // Old structure - use plan limits
    const basePlan = subscription.basePlan || subscription;
    const limits = basePlan.limits || {};
    
    return {
      firebase_operations: limits.firebase_operations || 0,
      storage_mb: limits.storage_mb || 0,
      sales_documents: limits.sales_documents || 0,
      parties: limits.parties || 0,
      hasTopups: false
    };
  } catch (error) {
    console.error('Error getting total quota:', error);
    return null;
  }
};

/**
 * Check if company has exceeded limits (updated for new structure)
 */
export const checkUsageLimits = async (db, companyId) => {
  try {
    // Get subscription with total entitlements
    const totalQuota = await getTotalAvailableQuota(db, companyId);
    
    if (!totalQuota) {
      // Fallback to old plan-based limits
      const [usage, plan] = await Promise.all([
        getCompanyUsage(db, companyId),
        getCompanyPlan(db, companyId)
      ]);
      
      if (!usage) return null;

      const canonicalOps = resolveCanonicalOperations(usage);
      const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.starter;
      
      return {
        operations: {
          current: canonicalOps,
          trackedCanonical: canonicalOps,
          limit: limits.operations,
          percentage: (canonicalOps / limits.operations) * 100,
          exceeded: canonicalOps >= limits.operations,
          remaining: Math.max(0, limits.operations - canonicalOps)
        },
        storage: {
          current: usage.storage || 0,
          limit: limits.storage,
          percentage: ((usage.storage || 0) / limits.storage) * 100,
          exceeded: (usage.storage || 0) >= limits.storage,
          remaining: Math.max(0, limits.storage - (usage.storage || 0))
        },
        plan: {
          name: limits.name,
          price: limits.price,
          currentPlan: plan
        }
      };
    }
    
    // New structure — billing counter lives on subscription.currentMonthUsage
    const [usage, subscriptionSnap] = await Promise.all([
      getCompanyUsage(db, companyId),
      originalGetDoc(originalDoc(db, `companies/${companyId}/subscription_details/subscription`))
    ]);

    if (!usage) {
      return null;
    }

    const subscription = subscriptionSnap.exists() ? subscriptionSnap.data() : {};
    const billingOps =
      subscription.currentMonthUsage?.firebase_operations ??
      resolveCanonicalOperations(usage);

    // Get plan info for display
    const plan = await getCompanyPlan(db, companyId);
    const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.starter;
    
    return {
      operations: {
        current: billingOps,
        trackedCanonical: resolveCanonicalOperations(usage),
        limit: totalQuota.firebase_operations,
        percentage: totalQuota.firebase_operations
          ? (billingOps / totalQuota.firebase_operations) * 100
          : 0,
        exceeded: billingOps >= totalQuota.firebase_operations,
        remaining: Math.max(0, totalQuota.firebase_operations - billingOps)
      },
      storage: {
        current: usage.storage || 0,
        limit: totalQuota.storage_mb,
        percentage: ((usage.storage || 0) / totalQuota.storage_mb) * 100,
        exceeded: (usage.storage || 0) >= totalQuota.storage_mb,
        remaining: Math.max(0, totalQuota.storage_mb - (usage.storage || 0))
      },
      plan: {
        name: limits.name,
        price: limits.price,
        currentPlan: plan
      },
      hasTopups: totalQuota.hasTopups
    };
  } catch (error) {
    console.error('Error checking usage limits:', error);
    return null;
  }
};

/**
 * Track an operation (buffered to reduce Firebase writes)
 */
export const trackOperation = async (db, companyId, operationType = 'read', count = 1) => {
  try {
    // Initialize buffer for company if not exists
    if (!operationBuffer[companyId]) {
      operationBuffer[companyId] = {
        reads: 0,
        writes: 0,
        deletes: 0,
        total: 0
      };
    }
    
    const normalizedType = normalizeOperationType(operationType);

    // Increment buffer
    const safeCount = Math.max(1, Number.isFinite(Number(count)) ? Number(count) : 1);
    operationBuffer[companyId][normalizedType] += safeCount;
    operationBuffer[companyId].total += safeCount;
    
    if (operationBuffer[companyId].total >= 50) {
      await flushOperationBuffer(db, companyId);
      if (operationFlushTimers[companyId]) {
        clearTimeout(operationFlushTimers[companyId]);
        delete operationFlushTimers[companyId];
      }
      return;
    }

    // ✅ Low-volume fix: flush after a short delay even if < 50 ops
    // This ensures months like Apr/May/Jun still show non-zero usage.
    if (!operationFlushTimers[companyId]) {
      operationFlushTimers[companyId] = setTimeout(() => {
        flushOperationBuffer(db, companyId).catch((err) => {
          console.error('Deferred flushOperationBuffer failed:', err);
        }).finally(() => {
          if (operationFlushTimers[companyId]) {
            clearTimeout(operationFlushTimers[companyId]);
            delete operationFlushTimers[companyId];
          }
        });
      }, 15000); // 15s debounce window
    }
  } catch (error) {
    console.error('Error tracking operation:', error);
  }
};

/**
 * Track storage usage
 */
export const trackStorage = async (db, companyId, sizeInMB, operation = 'add') => {
  try {
    const month = getCurrentMonth();
    const date = getCurrentDate();
    const usageRef = originalDoc(db, `companies/${companyId}/usage/${month}`);
    
    const storageChange = operation === 'add' ? sizeInMB : -sizeInMB;
    
    // First, get current data to calculate new totals
    const currentDoc = await originalGetDoc(usageRef);
    const currentData = currentDoc.exists() ? currentDoc.data() : {};
    
    await originalSetDoc(usageRef, {
      storage: (currentData.storage || 0) + storageChange,
      [`dailyUsage.${date}.storage`]: (currentData.dailyUsage?.[date]?.storage || 0) + storageChange,
      lastUpdated: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error tracking storage:', error);
  }
};

/**
 * Deduct usage with priority: Base Plan First, Then Top-ups (FIFO)
 * @param {Object} db - Firestore database
 * @param {string} companyId - Company ID
 * @param {string} resourceType - Type of resource (firebase_operations, storage_mb, etc.)
 * @param {number} amount - Amount to deduct
 * @returns {Promise<Object>} - Deduction result
 */
export const deductUsageWithPriority = async (db, companyId, resourceType, amount) => {
  try {
    const subscriptionRef = originalDoc(db, `companies/${companyId}/subscription_details/subscription`);
    const subscriptionSnap = await originalGetDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      return { success: false, usedOldMethod: true };
    }
    
    const subscription = subscriptionSnap.data();
    
    // Check if new structure
    if (!subscription.basePlan || !subscription.currentMonthUsage) {
      return { success: false, usedOldMethod: true };
    }
    
    const basePlan = subscription.basePlan;
    const currentUsage = subscription.currentMonthUsage || {};
    const usageBreakdown = currentUsage.usageBreakdown || { fromBasePlan: {}, fromTopups: {} };
    
    // Calculate base plan available
    const baseLimit = basePlan.limits[resourceType] || 0;
    const baseUsed = usageBreakdown.fromBasePlan?.[resourceType] || 0;
    const baseAvailable = Math.max(0, baseLimit - baseUsed);
    
    let amountToDeduct = amount;
    const deductions = [];
    
    // Step 1: Deduct from base plan first
    if (baseAvailable >= amountToDeduct) {
      // All can be deducted from base plan
      await originalUpdateDoc(subscriptionRef, {
        [`currentMonthUsage.${resourceType}`]: (currentUsage[resourceType] || 0) + amountToDeduct,
        [`currentMonthUsage.usageBreakdown.fromBasePlan.${resourceType}`]: baseUsed + amountToDeduct,
        'currentMonthUsage.lastUpdated': new Date().toISOString()
      });
      
      deductions.push({ source: 'basePlan', amount: amountToDeduct });
      return { success: true, deductions, source: 'basePlan' };
    }
    
    // Step 2: Exhaust base plan, then use topups
    if (baseAvailable > 0) {
      await originalUpdateDoc(subscriptionRef, {
        [`currentMonthUsage.${resourceType}`]: (currentUsage[resourceType] || 0) + baseAvailable,
        [`currentMonthUsage.usageBreakdown.fromBasePlan.${resourceType}`]: baseUsed + baseAvailable,
        'currentMonthUsage.lastUpdated': new Date().toISOString()
      });
      
      deductions.push({ source: 'basePlan', amount: baseAvailable });
      amountToDeduct -= baseAvailable;
    }
    
    // Step 3: Deduct from topups (FIFO - oldest first)
    const topups = subscription.topups || [];
    const activeTopups = topups
      .map((t, idx) => ({ ...t, originalIndex: idx }))
      .filter(t => t.status === 'active' && (t.remaining?.[resourceType] || 0) > 0)
      .sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
    
    for (const topup of activeTopups) {
      if (amountToDeduct <= 0) break;
      
      const topupAvailable = topup.remaining?.[resourceType] || 0;
      const toDeduct = Math.min(amountToDeduct, topupAvailable);
      
      // Update topup remaining
      const newRemaining = topupAvailable - toDeduct;
      const allResourcesZero = 
        (resourceType === 'firebase_operations' ? newRemaining : (topup.remaining?.firebase_operations || 0)) === 0 &&
        (resourceType === 'storage_mb' ? newRemaining : (topup.remaining?.storage_mb || 0)) === 0 &&
        (resourceType === 'sales_documents' ? newRemaining : (topup.remaining?.sales_documents || 0)) === 0 &&
        (resourceType === 'parties' ? newRemaining : (topup.remaining?.parties || 0)) === 0;
      
      await originalUpdateDoc(subscriptionRef, {
        [`topups.${topup.originalIndex}.remaining.${resourceType}`]: newRemaining,
        [`topups.${topup.originalIndex}.status`]: allResourcesZero ? 'exhausted' : 'active',
        [`currentMonthUsage.${resourceType}`]: (currentUsage[resourceType] || 0) + toDeduct,
        [`currentMonthUsage.usageBreakdown.fromTopups.${topup.topupId}.${resourceType}`]: 
          (usageBreakdown.fromTopups?.[topup.topupId]?.[resourceType] || 0) + toDeduct,
        'currentMonthUsage.lastUpdated': new Date().toISOString()
      });
      
      deductions.push({ source: topup.topupId, amount: toDeduct });
      amountToDeduct -= toDeduct;
    }
    
    if (amountToDeduct > 0) {
      console.warn(`⚠️ Could not deduct ${amountToDeduct} ${resourceType} - insufficient quota`);
    }
    
    return { 
      success: amountToDeduct === 0, 
      deductions,
      source: deductions.length > 1 ? 'basePlan+topups' : (deductions[0]?.source || 'unknown')
    };
    
  } catch (error) {
    console.error('❌ Error in deductUsageWithPriority:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Flush operation buffer to Firebase
 */
export const flushOperationBuffer = async (db, companyId) => {
  if (!operationBuffer[companyId] || operationBuffer[companyId].total === 0) {
    return;
  }

  const buffer = { ...operationBuffer[companyId] };

  try {
    const month = getCurrentMonth();
    const date = getCurrentDate();
    const usageRef = originalDoc(db, `companies/${companyId}/usage/${month}`);

    const currentDoc = await originalGetDoc(usageRef);
    const currentData = currentDoc.exists() ? currentDoc.data() : {};

    // Always persist usage statistics (independent of subscription deduction outcome)
    const mergedDaily = {
      ...currentData.dailyUsage,
      [date]: {
        ...currentData.dailyUsage?.[date],
        operations: (currentData.dailyUsage?.[date]?.operations || 0) + buffer.total
      }
    };

    const updateData = {
      operations: (currentData.operations || 0) + buffer.total,
      breakdown: {
        reads: (currentData.breakdown?.reads || 0) + buffer.reads,
        writes: (currentData.breakdown?.writes || 0) + buffer.writes,
        deletes: (currentData.breakdown?.deletes || 0) + buffer.deletes,
        uploads: currentData.breakdown?.uploads || 0
      },
      dailyUsage: mergedDaily,
      weeklyUsage: buildWeeklyUsageMap(mergedDaily),
      lastUpdated: serverTimestamp(),
      month
    };

    await originalSetDoc(usageRef, updateData, { merge: true });

    // Best-effort subscription quota deduction; must not block usage stats
    try {
      const priorityResult = await deductUsageWithPriority(
        db,
        companyId,
        'firebase_operations',
        buffer.total
      );
      if (!priorityResult.success && !priorityResult.usedOldMethod) {
        console.warn('Priority deduction incomplete (usage stats still saved)');
      }
    } catch (deductError) {
      console.warn('Priority deduction failed (usage stats still saved):', deductError);
    }

    operationBuffer[companyId] = {
      reads: 0,
      writes: 0,
      deletes: 0,
      total: 0
    };
    if (operationFlushTimers[companyId]) {
      clearTimeout(operationFlushTimers[companyId]);
      delete operationFlushTimers[companyId];
    }
  } catch (error) {
    console.error('Error flushing operation buffer:', error);
  }
};

/**
 * Force flush all buffers immediately
 */
export const forceFlushAllBuffers = async (db) => {
  const companyIds = Object.keys(operationBuffer);

  for (const companyId of companyIds) {
    await flushOperationBuffer(db, companyId);
  }
};

/**
 * Flush all buffers (call on app close/unmount)
 */
export const flushAllBuffers = async (db) => {
  const companyIds = Object.keys(operationBuffer);
  
  for (const companyId of companyIds) {
    await flushOperationBuffer(db, companyId);
  }
};

/**
 * Execute Firebase operation with tracking and limit checking
 */
export const executeWithTracking = async (db, companyId, operation, callback, checkLimits = true) => {
  try {
    // Check limits before operation
    if (checkLimits) {
      // Import subscription limits check
      const { isSubscriptionValid, checkUsageLimits: checkLimitsUtil } = await import('./subscriptionLimits');
      
      // First check subscription validity
      const validityCheck = await isSubscriptionValid(companyId);
      if (!validityCheck.valid) {
        const error = new Error(
          `Your subscription has expired. Please renew your plan to continue using the service.`
        );
        error.code = 'SUBSCRIPTION_EXPIRED';
        error.daysRemaining = validityCheck.daysRemaining;
        
        // Use ErrorNotificationService to handle and display error
        errorNotificationService.handleSubscriptionError(error, {
          context: 'UsageTracking',
          showNotification: true
        });
        
        throw error;
      }
      
      // Then check usage limits
      const limits = await checkUsageLimits(db, companyId);
      
      if (limits && limits.operations.exceeded) {
        const error = new Error(
          `Monthly operation limit exceeded! You've used ${limits.operations.current} of ${limits.operations.limit} operations. Please upgrade your plan to continue.`
        );
        error.code = 'OPERATION_LIMIT_EXCEEDED';
        error.limits = limits;
        
        // Use ErrorNotificationService to handle and display error
        errorNotificationService.handleSubscriptionError(error, {
          context: 'UsageTracking',
          showNotification: true
        });
        
        throw error;
      }
    }
    
    // Execute the actual operation
    const result = await callback();
    
    // Track operation (async, don't block)
    trackOperation(db, companyId, operation).catch(err => {
      console.error('Failed to track operation:', err);
    });
    
    return result;
  } catch (error) {
    // Re-throw the error to be handled by caller
    throw error;
  }
};

/**
 * Check storage limit before upload
 */
export const checkStorageLimit = async (db, companyId, fileSizeInMB) => {
  try {
    // First check subscription validity
    const { isSubscriptionValid } = await import('./subscriptionLimits');
    const validityCheck = await isSubscriptionValid(companyId);
    
    if (!validityCheck.valid) {
      const error = new Error(`Your subscription has expired. Please renew your plan to upload files.`);
      error.code = 'SUBSCRIPTION_EXPIRED';
      error.daysRemaining = validityCheck.daysRemaining;
      error.subscriptionExpired = true;
      
      // Use ErrorNotificationService to handle and display error
      errorNotificationService.handleSubscriptionError(error, {
        context: 'StorageLimitCheck',
        showNotification: true
      });
      
      return {
        allowed: false,
        message: `Your subscription has expired. Please renew your plan to upload files.`,
        subscriptionExpired: true,
        daysRemaining: validityCheck.daysRemaining
      };
    }
    
    const limits = await checkUsageLimits(db, companyId);
    
    if (!limits) {
      return { allowed: true };
    }
    
    const newTotal = limits.storage.current + fileSizeInMB;
    
    if (newTotal > limits.storage.limit) {
      const error = new Error(`Storage limit exceeded! Current: ${limits.storage.current.toFixed(2)}MB, File: ${fileSizeInMB.toFixed(2)}MB, Limit: ${limits.storage.limit}MB. Please delete files or upgrade your plan.`);
      error.code = 'STORAGE_LIMIT_EXCEEDED';
      error.limits = limits;
      
      // Use ErrorNotificationService to handle and display error
      errorNotificationService.handleSubscriptionError(error, {
        context: 'StorageLimitCheck',
        showNotification: true
      });
      
      return {
        allowed: false,
        message: `Storage limit exceeded! Current: ${limits.storage.current.toFixed(2)}MB, File: ${fileSizeInMB.toFixed(2)}MB, Limit: ${limits.storage.limit}MB. Please delete files or upgrade your plan.`,
        current: limits.storage.current,
        limit: limits.storage.limit,
        fileSize: fileSizeInMB
      };
    }
    
    return { allowed: true };
  } catch (error) {
    console.error('Error checking storage limit:', error);
    // Allow operation on error to prevent blocking
    return { allowed: true };
  }
};

/**
 * Initialize usage tracking for a company (creates initial document)
 */
export const initializeCompanyUsage = async (db, companyId) => {
  try {
    const month = getCurrentMonth();
    const usageRef = originalDoc(db, `companies/${companyId}/usage/${month}`);
    
    const usageDoc = await originalGetDoc(usageRef);
    
    if (!usageDoc.exists()) {
      await originalSetDoc(usageRef, {
        month,
        operations: 0,
        storage: 0,
        breakdown: {
          reads: 0,
          writes: 0,
          deletes: 0,
          uploads: 0
        },
        dailyUsage: {},
        lastUpdated: serverTimestamp(),
        limitExceeded: false,
        warningShown: false
      });
    }
  } catch (error) {
    console.error('Error initializing company usage:', error);
  }
};


